/**
 * CASCADE TRIGGER UTILITY
 * 
 * Single entry point for invoking the global board cascade.
 * All mutation paths that affect timing must funnel through this function.
 * 
 * Round 48 FIX-4: Debounce key is now ${shopId}:${scheduledDate} (no techId).
 * If a broader (all-tech) call arrives while a scoped call is pending, the
 * scope is promoted to all-tech. Only the last call in a 500ms window fires.
 * 
 * Round 48 FIX-8: Returns { ok, error? } instead of void so callers can
 * detect cascade failures.
 */
import { supabase } from '@/integrations/supabase/client';
import { checkTechnicianEODViolations } from '@/utils/eodChecker';
import { pushClientError } from '@/utils/clientErrorBuffer';

type CascadeResult = { ok: boolean; error?: string };

interface PendingCascade {
  timeout: ReturnType<typeof setTimeout>;
  techId: string | undefined;
  /**
   * Every caller coalesced into this window. Clearing a superseded call's timer
   * used to drop its `resolve` with it, so that caller's promise NEVER settled —
   * AddJobDialog awaited a cascade that LiveReshuffle had superseded and its
   * "Adding job…" spinner stayed forever. All of them settle with the one result.
   */
  resolvers: Array<(r: CascadeResult) => void>;
}

const pendingCascades = new Map<string, PendingCascade>();

async function runPostCascadeEODChecks(
  shopId: string,
  scheduledDate: string,
  techId?: string
): Promise<void> {
  try {
    if (techId) {
      await checkTechnicianEODViolations(techId, scheduledDate);
    } else {
      // OPS-A6: align active-tech predicate with the canonical smart-assign
      // shape at supabase/functions/smart-assign/index.ts:345-354.
      //
      // Pre-OPS-A6 used `.eq('is_active', true)` cast through `(supabase as
      // any)` — but the technicians table has NO `is_active` column
      // (canonical schema columns are `employment_status: string` and
      // `is_present: boolean | null`; verified in
      // src/integrations/supabase/types.ts:13413-13442). The `as any` cast
      // hid this from tsc; at runtime the PostgREST 400 was swallowed by
      // the outer try/catch, so the ALL-TECH branch of post-cascade EOD
      // checking has been silently DEAD.
      //
      // Canonical predicate:
      //   - DB filter: employment_status = 'Active' (employment relationship)
      //   - In-memory filter: is_present !== false (default-present;
      //     null is treated as present, mirroring smart-assign's
      //     `t.is_present !== false` rather than `=== true`)
      const { data } = await supabase
        .from('technicians')
        .select('id, is_present')
        .eq('shop_id', shopId)
        .eq('employment_status', 'Active');
      const techs = (data || []).filter((t) => t.is_present !== false);
      if (techs.length > 0) {
        await Promise.all(
          techs.map((t) => checkTechnicianEODViolations(t.id, scheduledDate))
        );
      }
    }
  } catch (eodErr) {
    console.warn('[triggerBoardCascade] EOD check after cascade failed:', eodErr);
  }
}

/**
 * Trigger the global board cascade for a shop/date.
 * Debounced: multiple calls within 500ms collapse into one.
 * Returns { ok: true } on success, { ok: false, error } on failure.
 *
 * @param anchorTicketIds Optional. Tickets the caller has just written
 *   authoritative `start_time + opti_finish` for. Cascade promotes these
 *   into its locked set so the walker advances the cursor to
 *   `anchor.opti_finish` without re-placing the anchor itself. Downstream
 *   flexible tickets then chain from `anchor.opti_finish + 1`. Used by
 *   useAutoBump's not-started branch to prevent cascade from overriding
 *   the AutoBump anchor's placement.
 */
interface PendingCascadeExt extends PendingCascade {
  anchorTicketIds?: string[];
}
export async function triggerBoardCascade(
  shopId: string,
  scheduledDate: string,
  techId?: string,
  anchorTicketIds?: string[]
): Promise<CascadeResult> {
  const key = `${shopId}:${scheduledDate}`;

  const existing = pendingCascades.get(key) as PendingCascadeExt | undefined;
  const resolvers = existing ? existing.resolvers : [];
  if (existing) {
    clearTimeout(existing.timeout);
    // Promote to all-tech if either call is broader (no techId), OR
    // if both calls are scoped to *different* techs — collapsing two
    // distinct tech scopes into one would silently drop the other tech's
    // cascade. Promoting to all-tech covers both.
    if (!techId || !existing.techId || existing.techId !== techId) {
      techId = undefined;
    }
    // Merge anchor ticket IDs across debounced calls so neither anchor is
    // silently dropped.
    if (existing.anchorTicketIds || anchorTicketIds) {
      anchorTicketIds = Array.from(new Set([
        ...(existing.anchorTicketIds || []),
        ...(anchorTicketIds || []),
      ]));
    }
  }

  return new Promise<CascadeResult>((resolveThis) => {
    resolvers.push(resolveThis);
    // Settle EVERY caller coalesced into this window with the single cascade's result.
    const resolve = (r: CascadeResult) => { for (const settle of resolvers) settle(r); };
    const timeout = setTimeout(async () => {
      const pending = pendingCascades.get(key) as PendingCascadeExt | undefined;
      const finalTechId = pending?.techId;
      const finalAnchorIds = pending?.anchorTicketIds;
      pendingCascades.delete(key);
      try {
        const { error } = await supabase.functions.invoke('cascade-board', {
          body: {
            shopId,
            scheduledDate,
            techId: finalTechId,
            ignoreLocks: false,
            ...(finalAnchorIds && finalAnchorIds.length > 0 ? { anchorTicketIds: finalAnchorIds } : {}),
          }
        });

        if (error) {
          // FIX-R132-MED-4: most callers `await triggerBoardCascade()`
          // without checking the returned `{ok, error}` (it's a best-effort
          // helper). Push to the client-error buffer so a stale board
          // condition is at least diagnosable from the next bug report.
          const errMsg = typeof error === 'string' ? error : error.message || 'Cascade failed';
          console.warn('[triggerBoardCascade] ⚠️ Cascade completed with error:', error);
          pushClientError({
            source: 'mutation',
            message: `[cascade-board] invoke returned error: ${errMsg}`,
            extra: { scheduledDate, techId: finalTechId },
            shopId,
          });
          resolve({ ok: false, error: errMsg });
        } else {
          console.log(`[triggerBoardCascade] ✅ Cascade completed for ${scheduledDate}${finalTechId ? ` tech=${finalTechId}` : ''}`);
          await runPostCascadeEODChecks(shopId, scheduledDate, finalTechId);
          resolve({ ok: true });
        }
      } catch (err) {
        // FIX-R132-MED-4: see above.
        const errMsg = err instanceof Error ? err.message : 'Cascade failed';
        console.error('[triggerBoardCascade] ❌ Cascade failed:', err);
        pushClientError({
          source: 'mutation',
          message: `[cascade-board] invoke threw: ${errMsg}`,
          stack: (err as { stack?: string } | undefined)?.stack,
          extra: { scheduledDate, techId: finalTechId },
          shopId,
        });
        resolve({ ok: false, error: errMsg });
      }
    }, 500);

    pendingCascades.set(key, { timeout, techId, anchorTicketIds, resolvers } as PendingCascadeExt);
  });
}
